import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import ResultDialogShell, {
  SectionHeader,
  CompetencyBadges,
} from '../search/ResultDialogShell';
import type { PersonResult } from '../../types/api';

interface PersonResultDialogProps {
  result: PersonResult | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PersonResultDialog({ result, open, onOpenChange }: PersonResultDialogProps) {
  const { t } = useTranslation('search');
  const [expandedProjects, setExpandedProjects] = useState<number[]>([]);

  if (!result) return null;

  const toggleRow = (idx: number) => {
    setExpandedProjects((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  const departmentLabel =
    result.department === result.faculty
      ? result.department
      : `${result.department} (${result.faculty})`;

  const totalResults = result.non_project_results_count ?? 0;
  const matchScore =
    typeof result.similarity === 'number' ? result.similarity.toFixed(2) : String(result.similarity);

  const header = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <DialogTitle className="truncate text-xl">{result.name}</DialogTitle>
          <DialogDescription className="mt-1 truncate">
            {departmentLabel}
          </DialogDescription>
        </div>
        <Button
          size="sm"
          variant="default"
          asChild
          title={t('dialog.viewProfileTooltip')}
        >
          <a
            href={`/person/${result.id}?from=modal`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dialog.viewProfile')}
            <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Card className="shadow-none">
          <CardContent className="px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dialog.matchScore')}
            </div>
            <div className="mt-0.5 text-base font-semibold tabular-nums text-primary">
              {matchScore}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none">
          <CardContent className="px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dialog.totalResults')}
            </div>
            <div className="mt-0.5 text-base font-semibold tabular-nums">
              {totalResults}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  return (
    <ResultDialogShell
      open={open}
      onOpenChange={onOpenChange}
      closeLabel={t('dialog.close')}
      header={header}
    >
      {result.competencies && result.competencies.length > 0 && (
        <section className="mb-6">
          <SectionHeader
            label={t('dialog.matchedCompetencies')}
            count={result.competencies.length}
            tooltip={t('dialog.matchedCompetenciesTooltip')}
          />
          <CompetencyBadges items={result.competencies} />
        </section>
      )}

      {result.related_projects && result.related_projects.length > 0 && (
        <section>
          <SectionHeader
            label={t('dialog.relatedResults')}
            count={result.related_projects.length}
            tooltip={t('dialog.relatedResultsTooltip')}
          />
          <div className="rounded-md border">
            <Table>
              <TableBody>
                {result.related_projects.map((proj, idx) => {
                  const expanded = expandedProjects.includes(idx);
                  return (
                    <Fragment key={idx}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => toggleRow(idx)}
                      >
                        <TableCell className="px-3 py-2 align-top">
                          <div className="flex items-start gap-2">
                            <ChevronRight
                              className={cn(
                                'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                                expanded && 'rotate-90',
                              )}
                            />
                            <a
                              href={`/outcome/${proj.id}?from=modal`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium text-primary hover:underline"
                            >
                              {proj.name}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell className="w-32 px-3 py-2 align-top text-xs text-muted-foreground">
                          {proj.type}
                        </TableCell>
                        <TableCell className="w-16 px-3 py-2 text-center align-top tabular-nums text-xs text-muted-foreground">
                          {proj.year}
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={3}
                            className="bg-muted/30 px-6 py-3 text-sm leading-relaxed text-muted-foreground"
                          >
                            {proj.description || t('dialog.noDescription')}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </section>
      )}
    </ResultDialogShell>
  );
}
